/**
 * RichTextEditor — Tiptap-backed markdown composer.
 *
 * Drop-in replacement for ``MarkdownComposer``: same prop shape, but
 * the user gets a WYSIWYG editor (bold/italic/lists/links/quote/code)
 * that serializes to markdown via the ``tiptap-markdown`` extension.
 * The server's ``_sanitize_markdown`` (nh3) is the security boundary —
 * Tiptap is purely the editing affordance.
 *
 * The toolbar is minimal by design (the design doc reserves Tiptap
 * starter-kit + nothing more). The placeholder doubles as the
 * accessibility label since the editable surface is a contenteditable
 * div, not a textarea (so a regular <label htmlFor> doesn't reach it).
 */
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import * as React from "react";
import { useEffect, useId, useRef } from "react";
import { Markdown } from "tiptap-markdown";

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  /** Min character count (mirrors the API's Pydantic validator). */
  minLength?: number;
  /** Max character count (mirrors the API's Pydantic validator). */
  maxLength?: number;
  /** Disables the editor (e.g. while submitting). */
  disabled?: boolean;
  /** Show character count under the editor. */
  showCount?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  label,
  placeholder,
  minLength,
  maxLength,
  disabled,
  showCount = true,
}: RichTextEditorProps): React.JSX.Element {
  const labelId = useId();
  const helpId = `${labelId}-help`;
  const countId = `${labelId}-count`;
  // Track the last value we emitted so we can ignore parent state
  // updates that came from our own onChange (otherwise Tiptap fights
  // the parent state — reset cursor on every keystroke).
  const lastEmitted = useRef<string>(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // The starter-kit defaults already cover the safe subset our
        // server-side nh3 allow-list permits (bold/italic/strike/code/
        // headings/lists/blockquote/horizontalRule/link). No further
        // gating needed at the editor layer.
      }),
      Markdown.configure({
        html: false,
        breaks: true,
        transformPastedText: true,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      // tiptap-markdown attaches .getMarkdown() to the storage namespace.
      // Tiptap's typed Storage map doesn't know about third-party
      // extensions; cast through the runtime shape.
      const storage = ed.storage as unknown as {
        markdown: { getMarkdown: () => string };
      };
      const md = storage.markdown.getMarkdown();
      lastEmitted.current = md;
      onChange(md);
    },
    immediatelyRender: false,
    editorProps: {
      attributes: {
        "aria-labelledby": labelId,
        "aria-describedby": `${helpId} ${countId}`,
        class:
          "prose prose-sm max-w-none min-h-[8rem] rounded-md border border-border bg-white px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60",
      },
    },
  });

  // Sync external value changes (e.g. parent resets to empty after a
  // successful submit). Skip when the parent's value matches what we
  // just emitted — that's our own update bouncing back.
  useEffect(() => {
    if (!editor) return;
    if (value === lastEmitted.current) return;
    editor.commands.setContent(value, { emitUpdate: false });
    lastEmitted.current = value;
  }, [editor, value]);

  // Keep the editor's editable state in sync with the disabled prop.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  const overLimit = maxLength !== undefined && value.length > maxLength;
  const underMin =
    minLength !== undefined && value.length > 0 && value.length < minLength;

  return (
    <div>
      <span id={labelId} className="block text-sm font-medium text-foreground">
        {label}
      </span>
      <div className="mt-1">
        <EditorContent editor={editor} />
      </div>
      {placeholder && value.length === 0 && (
        // Tiptap doesn't render a real placeholder unless we install the
        // placeholder extension; for now use a hint line so the field
        // never feels empty without further context.
        <p className="mt-1 text-xs italic text-muted-foreground">
          {placeholder}
        </p>
      )}
      <p id={helpId} className="mt-1 text-xs text-muted-foreground">
        Rich text: select to format. Bold (Ctrl/Cmd+B), italic (Ctrl/Cmd+I),
        lists, quotes, links all supported.
      </p>
      {showCount && maxLength && (
        <p
          id={countId}
          className={`mt-0.5 text-xs ${
            overLimit
              ? "text-red-700"
              : underMin
                ? "text-yellow-700"
                : "text-muted-foreground"
          }`}
          aria-live="polite"
        >
          {value.length} / {maxLength}
        </p>
      )}
    </div>
  );
}
